import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated API outside src/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: customer pages use generated dataModel outside src/
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteConfirmDialog } from '@/components/insurance/DeleteConfirmDialog';
import { Plus, ArrowLeft, ClipboardList, Clock, CheckCircle2, Archive, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TClaimStatus, IMyClaimsStatsCardProps } from '@/@types/claims';

const POLICY_TYPE_COLORS: Record<string, string> = {
  health: 'bg-green-500',
  car: 'bg-blue-500',
  motor: 'bg-blue-500',
  travel: 'bg-amber-500',
  home: 'bg-emerald-600',
  life: 'bg-purple-500',
};

function policyTypeDotColor(typeName: string): string {
  const key = typeName.toLowerCase();
  return POLICY_TYPE_COLORS[key] ?? 'bg-gray-400';
}

function claimStatusLabel(status: TClaimStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function claimStatusClass(status: TClaimStatus): string {
  switch (status) {
    case 'approved':
    case 'paid':
      return 'text-green-600 bg-green-50 px-2.5 py-0.5 rounded-full';
    case 'under_review':
    case 'submitted':
      return 'text-orange-500 bg-orange-50 px-2.5 py-0.5 rounded-full';
    case 'rejected':
      return 'text-red-600 bg-red-50 px-2.5 py-0.5 rounded-full';
    case 'draft':
      return 'text-blue-500 bg-blue-50 px-2.5 py-0.5 rounded-full';
    default:
      return 'text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full';
  }
}

function formatDate(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatAmount(amount: number, currency: string): string {
  const symbol = currency === 'EUR' ? '\u20ac' : currency === 'USD' ? '$' : currency;
  return `${symbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function MyClaimsListPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';

  const claims = useQuery(api.insurance.claims.listMy);
  const batchDelete = useMutation(api.insurance.claims.batchDeleteDrafts);

  const draftClaims = (claims ?? []).filter(c => c.status === 'draft');

  const onDelete = useCallback(
    async (ids: string[]) => {
      await batchDelete({
        claimIds: ids as Id<'claims'>[], // AS_JUSTIFIED: ids come from claim._id which are Id<'claims'>
      });
    },
    [batchDelete]
  );

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useBatchSelection(draftClaims, onDelete);

  if (claims === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const totalClaims = claims.length;
  const inReview = claims.filter(c => {
    return c.status === 'under_review' || c.status === 'submitted';
  }).length;
  const approved = claims.filter(c => {
    return (
      c.status === 'approved' ||
      c.status === 'paid' ||
      c.status === 'partially_approved' ||
      c.status === 'partially_paid'
    );
  }).length;
  const closedOrDraft = claims.filter(c => {
    return c.status === 'rejected' || c.status === 'draft';
  }).length;

  return (
    <div className="flex-1 p-8">
      <Button variant="ghost" size="sm" className="mb-2" onClick={() => navigate(basePath)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">My Claims</h1>
        <div className="flex items-center gap-2">
          {selectedCount > 0 && (
            <Button variant="destructive" size="sm" onClick={() => setIsDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete ({selectedCount})
            </Button>
          )}
          {claims.length > 0 && (
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/80"
              onClick={() => navigate(`${basePath}/claims/new`)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Claim
            </Button>
          )}
        </div>
      </div>

      {claims.length === 0 ? (
        <ClaimsEmptyState onNewClaim={() => navigate(`${basePath}/claims/new`)} />
      ) : (
        <ClaimsPopulatedState
          claims={claims}
          basePath={basePath}
          stats={{ totalClaims, inReview, approved, closedOrDraft }}
          selectedIds={selectedIds}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
          hasDrafts={draftClaims.length > 0}
        />
      )}

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedCount}
        entityLabel="claim draft"
      />
    </div>
  );
}

// NEW_FUNCTION_JUSTIFIED: ClaimsEmptyState — empty state sub-component extracted to keep MyClaimsListPage under 200 lines
function ClaimsEmptyState({ onNewClaim }: { onNewClaim: () => void }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop for trivial single-prop private sub-component
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-muted mb-4">
        <ClipboardList className="h-8 w-8 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold mb-1">No claims yet</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        You haven&apos;t submitted any insurance claims. When you file a claim, it will appear here
        for tracking.
      </p>
      <Button
        className="bg-primary text-primary-foreground hover:bg-primary/80"
        onClick={onNewClaim}
      >
        <Plus className="h-4 w-4 mr-2" />
        New Claim
      </Button>
    </div>
  );
}

// NEW_FUNCTION_JUSTIFIED: ClaimsPopulatedState — populated state sub-component with stats cards and table
function ClaimsPopulatedState({
  // LOCAL_TYPE_JUSTIFIED: inline destructured props for private sub-component tightly coupled to parent
  claims,
  basePath,
  stats,
  selectedIds,
  onSelectRow,
  onSelectAll,
  isAllSelected,
  isSomeSelected,
  hasDrafts,
}: {
  // LOCAL_TYPE_JUSTIFIED: inline type for private sub-component props
  claims: Array<{
    _id: string;
    claimNumber: string;
    status: string;
    insuranceTypeName?: string;
    providerName?: string;
    claimantName?: string;
    description?: string;
    totalAmountClaimed?: number;
    currency?: string;
    dateOfService?: number;
    createdAt: number;
  }>;
  basePath: string;
  stats: { totalClaims: number; inReview: number; approved: number; closedOrDraft: number };
  selectedIds: Set<string>;
  onSelectRow: (id: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  hasDrafts: boolean;
}) {
  const navigate = useNavigate();

  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatsCard icon={ClipboardList} label="Total Claims" value={stats.totalClaims} />
        <StatsCard
          icon={Clock}
          label="In Review"
          value={stats.inReview}
          iconClass="text-orange-500"
          valueClass="text-orange-500"
        />
        <StatsCard
          icon={CheckCircle2}
          label="Approved"
          value={stats.approved}
          iconClass="text-green-600"
          valueClass="text-green-600"
        />
        <StatsCard icon={Archive} label="Closed / Draft" value={stats.closedOrDraft} />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                {hasDrafts && (
                  <Checkbox
                    checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                    onCheckedChange={onSelectAll}
                    aria-label="Select all draft claims"
                  />
                )}
              </TableHead>
              <TableHead>Claim ID</TableHead>
              <TableHead>Policy Type</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Claimant</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Date Filed</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claims.map(claim => {
              const isDraft = claim.status === 'draft';
              const href = isDraft
                ? `${basePath}/claims/new?draftId=${claim._id}`
                : `${basePath}/claims/${claim._id}`;
              const statusTyped = claim.status as TClaimStatus; // AS_JUSTIFIED: Convex return status string narrowed to TClaimStatus for styling and label
              return (
                <TableRow
                  key={claim._id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(href)}
                >
                  <TableCell onClick={e => e.stopPropagation()}>
                    {isDraft && (
                      <Checkbox
                        checked={selectedIds.has(claim._id)}
                        onCheckedChange={checked => onSelectRow(claim._id, !!checked)}
                        aria-label={`Select claim ${claim.claimNumber}`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-blue-600">
                    {claim.claimNumber}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'h-2.5 w-2.5 rounded-full shrink-0',
                          policyTypeDotColor(claim.insuranceTypeName ?? '')
                        )}
                      />
                      <span className="text-sm">{claim.insuranceTypeName ?? '\u2014'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{claim.providerName ?? '\u2014'}</TableCell>
                  <TableCell className="text-sm">{claim.claimantName ?? '\u2014'}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">
                    {claim.description ?? '\u2014'}
                  </TableCell>
                  <TableCell className="text-sm text-right font-medium">
                    {formatAmount(claim.totalAmountClaimed ?? 0, claim.currency ?? 'EUR')}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {claim.dateOfService
                      ? formatDate(claim.dateOfService)
                      : formatDate(claim.createdAt)}
                  </TableCell>
                  <TableCell>
                    <span className={cn('text-sm font-medium', claimStatusClass(statusTyped))}>
                      {claimStatusLabel(statusTyped)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

// NEW_FUNCTION_JUSTIFIED: StatsCard sub-component displays claim count per category with icon
function StatsCard({ icon: Icon, label, value, iconClass, valueClass }: IMyClaimsStatsCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Icon className={cn('h-5 w-5 text-muted-foreground', iconClass)} />
        <div>
          <p className={cn('text-2xl font-bold', valueClass)}>{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  );
}
